import { Button } from '@bambi/component-library/src/components/Button';
import { classNames } from '@bambi/component-library/src/utils/classNames';
import { DateTime } from 'luxon';
import { useCallback, useState, type ComponentType } from 'react';

import { type NotificationRendererProps } from './renderers/types';
import { useAppDispatch } from 'src/app/store/hooks';
import {
  useNotificationsNotificationsPartialUpdateMutation,
  type NotificationRead,
} from 'src/common/external/bambi-api/notificationsApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

interface NotificationItemProps {
  title: React.ReactNode;
  notification: NotificationRead;
  initialOpen?: boolean;
  allowToggleOpen?: boolean;
  allowMarkAsRead?: boolean;
  onDismiss?: () => void;
  Renderer: ComponentType<NotificationRendererProps>;
}

export function NotificationItem({
  title,
  notification,
  initialOpen = false,
  allowToggleOpen = true,
  allowMarkAsRead = true,
  onDismiss,
  Renderer,
}: NotificationItemProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(initialOpen);

  const handleToggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  const [trigger] = useNotificationsNotificationsPartialUpdateMutation();

  const handleMarkAsRead = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        await trigger({
          id: notification.id,
          patchedNotification: {
            read_at: DateTime.now().toISO(),
          },
        }).unwrap();
        onDismiss?.();
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to dismiss notification',
            description: formatServerError(e),
          })
        );
      }
    },
    [trigger, notification, dispatch, onDismiss]
  );

  const toggleOpenProps: React.HTMLAttributes<HTMLDivElement> = allowToggleOpen
    ? {
        'aria-label': 'Click to expand or collapse notification',
        role: 'button',
        tabIndex: 0,
        'aria-expanded': open,
        onClick: handleToggleOpen,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleOpen();
          }
        },
      }
    : {};

  return (
    <div
      className={classNames(
        'flex flex-row justify-between gap-2',
        allowToggleOpen && 'cursor-pointer'
      )}
      {...toggleOpenProps}
    >
      <div className="flex flex-col gap-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-slate-400">
          {DateTime.fromISO(notification.created_at).toRelative()}
        </div>
        {open ? <Renderer notification={notification} /> : null}
      </div>
      {allowMarkAsRead ? (
        <Button
          variant="secondary-outline"
          className="max-h-[40px] whitespace-nowrap"
          onClick={handleMarkAsRead}
        >
          Dismiss
        </Button>
      ) : null}
    </div>
  );
}
